import util from '../../lib/util';
import store from '../store';
import Actions from '../actions/indexExport';
import {getCurrentUserId} from '../../lib/picos/currentLogin';
import newHRTimestamp from '../../lib/picos/timestamp';
import {findAll} from "../find";
import DeadlineRule from '../../entities/deadlineRule';
import Deadline from '../../entities/deadline';
import CourtDeadlineRule from '../../entities/courtDeadlineRule';

/*
 * Delete/nullify anywhere this deadlineRule is used in other entities
 *
 * @param {DeadlineRule} deadlineRule
 */
export function deleteDeadlineRule(deadlineRule: DeadlineRule, mergeId?: string) {
    let targetImportSource = 'merge';

    if (!mergeId) {
        targetImportSource = 'delete';
        mergeId = deadlineRule.id;
    }
    /* Simple delete usages */
        
    store.getState().deadlines
        .getMappingFor('deadlineRuleId', deadlineRule.id)
        .filter(deadlineTemp => deadlineTemp.status !== 'D')
        .forEach(deadlineTemp => {
            const _deadline = deadlineTemp.clone();
            _deadline.status =  'U';
            _deadline.addedOn = newHRTimestamp();
            _deadline.addedBy = getCurrentUserId();
            _deadline.importSource = targetImportSource;
            _deadline.importId = mergeId;

            _deadline.deadlineRuleId = null;
            store.dispatch(Actions.DeadlineActions.saveDeadlineQueue(_deadline));
        });

    store.getState().deadlineRules
        .getMappingFor('deadlineRuleId', deadlineRule.id)
        .filter(deadlineRuleTemp => deadlineRuleTemp.status !== 'D')
        .forEach(deadlineRuleTemp => {
            const _deadlineRule = deadlineRuleTemp.clone();
            _deadlineRule.status =  'U';
            _deadlineRule.addedOn = newHRTimestamp();
            _deadlineRule.addedBy = getCurrentUserId();
            _deadlineRule.importSource = targetImportSource;
            _deadlineRule.importId = mergeId;

            _deadlineRule.triggerRuleId = null;
            store.dispatch(Actions.DeadlineRuleActions.saveDeadlineRuleQueue(_deadlineRule));
        });

    store.getState().courtDeadlineRules
        .getMappingFor('deadlineRuleId', deadlineRule.id)
        .filter(courtDeadlineRuleTemp => courtDeadlineRuleTemp.status !== 'D')
        .forEach(courtDeadlineRuleTemp => {
            const _courtDeadlineRule = courtDeadlineRuleTemp.clone();
            _courtDeadlineRule.status =  'D';
            _courtDeadlineRule.addedOn = newHRTimestamp();
            _courtDeadlineRule.addedBy = getCurrentUserId();
            _courtDeadlineRule.importSource = targetImportSource;
            _courtDeadlineRule.importId = mergeId;

            store.dispatch(Actions.CourtDeadlineRuleActions.saveCourtDeadlineRuleQueue(_courtDeadlineRule));
        });

    /* Remove the actual deadlineRule */
    deadlineRule.status = 'D';
    deadlineRule.addedOn = newHRTimestamp();
    deadlineRule.addedBy = getCurrentUserId();
    deadlineRule.importSource = targetImportSource;
    deadlineRule.importId = mergeId;
    store.dispatch(Actions.DeadlineRuleActions.removeDeadlineRuleQueue(deadlineRule));
    
}

/*
 * Replace anywhere this deadlineRule is used in other entities.
 *
 * @param {DeadlineRule} deadlineRule
 * @param {DeadlineRule} replaceWith
 * @return {DeadlineRule} - DeadlineRule will have refs stripped away
 */
export function replaceDeadlineRule(deadlineRule: DeadlineRule, replaceWith: DeadlineRule, mergeId?: string) {
    let targetImportSource = 'merge';

    if (!mergeId) {
        targetImportSource = 'delete';
        mergeId = deadlineRule.id;
    }
    /* Simple replace usages */
        
    store.getState().deadlines
        .getMappingFor('deadlineRuleId', deadlineRule.id)
        .filter(deadlineTemp => deadlineTemp.status !== 'D')
        .forEach(deadlineTemp => {
            const _deadline = deadlineTemp.clone();
            _deadline.status =  'U';
            _deadline.addedOn = newHRTimestamp();
            _deadline.addedBy = getCurrentUserId();
            _deadline.importSource = targetImportSource;
            _deadline.importId = mergeId;

            _deadline.deadlineRuleId = replaceWith.id;
            store.dispatch(Actions.DeadlineActions.saveDeadlineQueue(_deadline));
        });

    store.getState().deadlineRules
        .getMappingFor('deadlineRuleId', deadlineRule.id)
        .filter(deadlineRuleTemp => deadlineRuleTemp.status !== 'D')
        .forEach(deadlineRuleTemp => {
            const _deadlineRule = deadlineRuleTemp.clone();
            _deadlineRule.status =  'U';
            _deadlineRule.addedOn = newHRTimestamp();
            _deadlineRule.addedBy = getCurrentUserId();
            _deadlineRule.importSource = targetImportSource;
            _deadlineRule.importId = mergeId;

            _deadlineRule.triggerRuleId = replaceWith.id;
            store.dispatch(Actions.DeadlineRuleActions.saveDeadlineRuleQueue(_deadlineRule));
        });

    store.getState().courtDeadlineRules
        .getMappingFor('deadlineRuleId', deadlineRule.id)
        .filter(courtDeadlineRuleTemp => courtDeadlineRuleTemp.status !== 'D')
        .forEach(courtDeadlineRuleTemp => {
            const _courtDeadlineRule = courtDeadlineRuleTemp.clone();
            _courtDeadlineRule.status =  'U';
            _courtDeadlineRule.addedOn = newHRTimestamp();
            _courtDeadlineRule.addedBy = getCurrentUserId();
            _courtDeadlineRule.importSource = targetImportSource;
            _courtDeadlineRule.importId = mergeId;

            _courtDeadlineRule.deadlineRuleId = replaceWith.id;
            store.dispatch(Actions.CourtDeadlineRuleActions.saveCourtDeadlineRuleQueue(_courtDeadlineRule));
        });

    return deadlineRule;
}

/*
 * Determine the impact of deleting this deadlineRule from other entities
 *
 * @param {DeadlineRule} deadlineRule
 * @return {Array.<{count: number, entity: string}>} - a list of impacted entities, along with a count of impacted entities
 */
export function deleteImpactDeadlineRule(deadlineRule: DeadlineRule) {
    const impacts: string[] = [];
    const state = store.getState();
    
    /* Simple delete usages */
        
    const deadlineDeadlineRuleIdImpact = state.deadlines
          .getMappingFor('deadlineRuleId', deadlineRule.id)
          .filter(deadline => deadline.status !== 'D');

    deadlineDeadlineRuleIdImpact.forEach(() => impacts.push('deadlines'));

    const deadlineRuleTriggerRuleIdImpact = state.deadlineRules
          .getMappingFor('deadlineRuleId', deadlineRule.id)
          .filter(deadlineRule => deadlineRule.status !== 'D');

    deadlineRuleTriggerRuleIdImpact.forEach(() => impacts.push('deadlineRules'));

    const courtDeadlineRuleDeadlineRuleIdImpact = state.courtDeadlineRules
          .getMappingFor('deadlineRuleId', deadlineRule.id)
          .filter(courtDeadlineRule => courtDeadlineRule.status !== 'D');

    courtDeadlineRuleDeadlineRuleIdImpact.forEach(() => impacts.push('courtDeadlineRules'));

    return util.countDistinct(impacts);
}